import React, { useState, useEffect } from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@src/common/Button';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { event, event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import isEmpty from 'lodash/isEmpty';
import { getErrorFields } from '@src/state/tinRegistrationStatus/utils';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { config } from '@uhg-abyss/web/tools/config';
import { useUser } from '@src/state/user/hooks';
import { PropTypes } from 'prop-types';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Loading } from '@src/common/Loading';
import { storage } from '@uhg-abyss/web/tools/storage';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { SelectRegistrationSearchCriteria } from './Inputs/SelectRegistrationSearchCriteria';
import { ByTin } from './Inputs/ByTin';
import { ByRequestId } from './Inputs/ByRequestId';
import { ByTrackingNumber } from './Inputs/ByTrackingNumber';

const BoxHeader = styled('div', {
  position: 'relative',
  height: '120px',
  marginTop: '-62px',
  borderBottom: '1px solid #dadbdc',
});

const ClipBoardIcon = styled('div', {
  display: 'block',
  width: '50px',
  margin: 'auto',
  marginTop: '14px',
});

const Heading = styled('div', {
  fontWeight: 600,
  fontSize: '22px',
  textAlign: 'center',
  paddingTop: '70px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const InputField = styled('div', {
  margin: '-6px 20px 20px 20px',
});

const InputDescription = styled('p', {
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 500,
  padding: '20px',
  fontFamily: '$primary',
});

const CheckStatusButton = styled('div', {
  margin: 'auto',
  width: '80%',
  paddingTop: '25px',
  paddingBottom: '26px',
  fontWeight: 600,
  fontSize: '16px',
  color: ({ theme }) => {
    return '$primary1';
  },
  display: 'flex',
  justifyContent: 'center',
  '& button': {
    width: '85%',
    paddingLeft: 'inherit',
  },
});

const Layout = styled('div', {
  height: '100%',
  margin: 'auto',
  boxShadow: '0 0 3px 0 rgba(0, 0, 0, 0.5)',
  borderRadius: '3px',
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
});

const HeaderAndInput = styled('div', {
  height: '75%',
});

export const TinRegistrationStatus = ({
  setIsAlertBanner,
  setTinRegStatusError,
  setTinRegError,
}) => {
  const form = useForm();
  const [isTin, setIsTin] = useState(false);
  const [isRequestId, setRequestId] = useState(false);
  const [isTrackingNum, setIsTrackingNum] = useState(false);
  const { searchRegistrationStatus } = useTinRegistrationStatus.actions();
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();
  const userInfo = useUser.state();
  const { navigate } = useRouter();
  const errorMessage = 'We were unable to retrieve your TIN registration. ';
  const isSuperUser = storage.local.get(userInfo.uuid);
  const handleCheckStatus = () => {
    setIsAlertBanner(false);
    setTinRegStatusError(false);
    form.validate(
      'searchCriteria',
      (values) => {
        const { registrationOption, tin, requestId, trackingNumber } = values;
        const value = values.registrationOption;
        if (!value) {
          form.setError('searchCriteria.registrationOption', {
            type: 'manual',
            message: 'Please select one.',
          });
        } else {
          adobeTaggingEvent('START_TIN_REGISTRATION_STATUS', { value });
          searchRegistrationStatus(
            {
              isSuperUser: config('US3671382_SUPER_USER') && !!isSuperUser,
              billingTin: registrationOption === 'tin' ? tin : null,
              requestId: registrationOption === 'requestId' ? requestId : null,
              isAllSelected: registrationOption === 'isAllSelected',
              trackingNumber:
                registrationOption === 'referenceNumber'
                  ? trackingNumber
                  : null,
            },
            (response) => {
              if (registrationOption === 'isAllSelected') {
                navigate('/multitin-registration-status');
              } else {
                navigate('/tin-registration-status');
              }
            },
            (error) => {
              event('API_TRANSACTION_ERROR', {
                code: error.faultCode,
                message: errorMessage + error.message,
              });
              setTinRegError(false);
              setIsAlertBanner(true);
              setTinRegStatusError(true);
            }
          );
        }
      },
      (error) => {
        const fields = getErrorFields(error);
        if (!isEmpty(fields)) {
          adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };
  const handleOption = (selected) => {
    setIsTin(selected.tin);
    setRequestId(selected.requestId);
    setIsTrackingNum(selected.trackingNum);
  };

  useEffect(() => {}, [isTrackingNum, isRequestId, isTin]);

  return (
    <Loading
      isLoading={searchRegistrationStatusSaga.isLoading}
      style={{ height: '100%' }}
    >
      <Layout>
        <HeaderAndInput>
          <ClipBoardIcon>
            <IconBrand
              icon="magnifying_glass"
              size="49px"
              variant="twotonelightcircle"
            />
          </ClipBoardIcon>
          <BoxHeader>
            <Heading id="dashboard-tinRegistrationStatus">
              TIN registration status
            </Heading>
          </BoxHeader>
          <InputDescription id="dashboard-tinRegistrationStatusText">
            If you have submitted your TIN registration, you can check the
            status here by choosing from the menu below.
          </InputDescription>
          <Grid>
            <Grid.Col span="50%">
              <InputField>
                <SelectRegistrationSearchCriteria
                  selectedOption={handleOption}
                  autoComplete="off"
                  inputModel="searchCriteria.registrationOption"
                  inputLabel="select-registration-criteria-input"
                />
              </InputField>
            </Grid.Col>
            <Grid.Col span="50%">
              {isTin && (
                <InputField>
                  <ByTin
                    inputModel="searchCriteria.tin"
                    id="searchCriteria-tin-input"
                    inputLabel="tinField"
                  />
                </InputField>
              )}
              {isRequestId && (
                <InputField>
                  <ByRequestId
                    inputModel="searchCriteria.requestId"
                    id="searchCriteria-request-id-input"
                    inputLabel="requestIdField"
                  />
                </InputField>
              )}
              {isTrackingNum && (
                <InputField>
                  <ByTrackingNumber
                    inputModel="searchCriteria.trackingNumber"
                    id="searchCriteria-trackingNumber-input"
                    inputLabel="trackingNumberField"
                    required
                  />
                </InputField>
              )}
            </Grid.Col>
          </Grid>
        </HeaderAndInput>
        <CheckStatusButton>
          <Button
            type="button"
            id="checkstatus-button"
            aria-label="click here to check TIN registration status"
            data-testid="start-button"
            onClick={handleCheckStatus}
            width="100%"
          >
            Check Status
          </Button>
        </CheckStatusButton>
      </Layout>
    </Loading>
  );
};

TinRegistrationStatus.propTypes = {
  setIsAlertBanner: PropTypes.func.isRequired,
  setTinRegStatusError: PropTypes.func.isRequired,
  setTinRegError: PropTypes.func.isRequired,
};

TinRegistrationStatus.defaultProps = {};
